import React from 'react';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageLoading } from '../../../../components/PageLoading';
import { SidePanel } from '../../../../components/SidePanel';

import { Header } from './Header';
import { useOrderDetails } from './hooks/useOrderDetails';
import { useTransmissionEvents } from './hooks/useTransmissionEvents';
import { InternalReporting } from './InternalReporting';
import { ProductsTable } from './ProductsTable';
import { TransmissionEventTable } from './TransmissionEventTable';
import { OrderDetailsModalProps } from './types';

export const OrderDetailsPanel: React.FC<OrderDetailsModalProps> = ({
  orderId,
  isOpen,
  onClose,
}) => {
  const {
    order,
    loading: orderLoading,
    error: orderError,
  } = useOrderDetails(orderId);

  const {
    transmissionEvents,
    loading: transmissionEventsLoading,
    error: transmissionEventsError,
  } = useTransmissionEvents(orderId);

  const isLoading = orderLoading || transmissionEventsLoading;

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      className="overflow-hidden"
    >
      <div className="relative h-full">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-content1/80 z-10">
            <PageLoading />
          </div>
        )}

        <div className="p-6 space-y-6 h-full overflow-y-auto">
          {/* Header Section */}
          <Header order={order} isLoading={orderLoading} />

          {/* Internal Reporting Section */}
          {order && order.isTest !== null && order.isTest !== undefined && (
            <InternalReporting orderId={order.id} isTest={order.isTest} />
          )}

          {/* Transmission Events Section */}
          {transmissionEventsError ? (
            <ErrorCard
              title="Failed to load transmission events"
              message={
                transmissionEventsError.message ||
                'An error occurred while loading transmission events.'
              }
            />
          ) : (
            <TransmissionEventTable
              isLoading={transmissionEventsLoading}
              transmissionEvents={transmissionEvents || []}
              orderCreatedAt={order?.createdAt}
            />
          )}

          {/* Products Table Section */}
          {orderError ? (
            <ErrorCard
              title="Failed to load order details"
              message={
                orderError.message ||
                'An error occurred while loading order details.'
              }
            />
          ) : (
            <ProductsTable
              isLoading={orderLoading}
              products={order?.products || []}
            />
          )}
        </div>
      </div>
    </SidePanel>
  );
};
